import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FinancialCashOverview, isFinancialPreviewEnabled, readFinancialCashOverview } from '../../services/FinancialReadService';

const labels: Record<string, string> = {
  receipt: 'Recebimento',
  reversal: 'Estorno',
};

function monthBounds(month: string): { from: string; until: string } {
  const [year, monthNumber] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return { from: `${month}-01`, until: `${month}-${String(lastDay).padStart(2, '0')}` };
}

function formatAmount(amount: unknown): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(amount ?? 0));
}

function formatDate(value: string): string {
  return value.includes('T') ? new Date(value).toLocaleString('pt-BR') : value;
}

function cashMovementType(movement: Pick<FinancialCashOverview['movements'][number], 'movementType' | 'movementDirection'>): string {
  return movement.movementType === 'receipt' && movement.movementDirection === 'out'
    ? 'reversal'
    : movement.movementType;
}

function cashMovementLabel(movement: Pick<FinancialCashOverview['movements'][number], 'movementType' | 'movementDirection'>): string {
  const type = cashMovementType(movement);
  return labels[type] ?? type;
}

function CashOverviewPanel({ clientId }: { clientId: string }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [overview, setOverview] = useState<FinancialCashOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    if (!month) return;
    setLoading(true); setError('');
    try {
      const bounds = monthBounds(month);
      setOverview(await readFinancialCashOverview(clientId, bounds.from, bounds.until));
    } catch (cause) {
      setOverview(null);
      setError(cause instanceof Error ? cause.message : 'Não foi possível carregar o fluxo de caixa.');
    } finally { setLoading(false); }
  }

  useEffect(() => { void load(); }, [clientId, month]);
  const totals = overview?.totals;
  const summaryByType: Record<string, { movementType: string; inflow: number; outflow: number }> = {};
  overview?.movements.forEach((movement) => {
    const movementType = cashMovementType(movement);
    const current = summaryByType[movementType] ?? { movementType, inflow: 0, outflow: 0 };
    if (movement.movementDirection === 'in') current.inflow += Number(movement.amount);
    else current.outflow += Number(movement.amount);
    summaryByType[movementType] = current;
  });
  const movementSummary = Object.values(summaryByType);
  const cashDifference = totals
    ? totals.openingBalance + totals.inflow - totals.outflow - totals.closingBalance
    : 0;

  function exportCsv() {
    if (!overview) return;
    const escapeCsv = (item: unknown) => `"${String(item ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Período', `${overview.periodFrom} a ${overview.periodUntil}`],
      ['Saldo inicial', totals?.openingBalance ?? 0],
      ['Entradas', totals?.inflow ?? 0],
      ['Saídas', totals?.outflow ?? 0],
      ['Saldo final', totals?.closingBalance ?? 0],
      [],
      ['Data', 'Conta', 'Tipo', 'Descrição', 'Direção', 'Valor'],
      ...overview.movements.map((movement) => [
        movement.occurredAt,
        movement.accountName,
        cashMovementLabel(movement),
        movement.description ?? '',
        movement.movementDirection === 'in' ? 'Entrada' : 'Saída',
        movement.amount,
      ]),
    ];
    const csv = `\ufeff${rows.map(row => row.map(escapeCsv).join(';')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-caixa-${clientId}-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return <section className="rounded-lg border bg-white p-5 space-y-4">
    <div className="flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-lg font-semibold text-gray-900">Fluxo de caixa</h2><p className="mt-1 text-sm text-gray-600">Movimentações efetivas do período, separadas de faturas emitidas.</p></div><div className="flex flex-wrap items-end gap-3"><label className="text-sm">Competência<input aria-label="Competência do caixa" type="month" className="mt-1 block rounded border p-2" value={month} onChange={event => setMonth(event.target.value)} /></label><button type="button" disabled={!overview || loading} onClick={exportCsv} className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 disabled:cursor-not-allowed disabled:opacity-50">Exportar relatório CSV</button></div></div>
    {error && <p role="alert" className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
    {loading ? <p role="status" className="text-sm text-gray-600">Carregando fluxo de caixa…</p> : overview && <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"><div className="rounded border bg-gray-50 p-3"><span className="text-xs text-gray-500">Saldo inicial</span><strong className="mt-1 block">{formatAmount(totals?.openingBalance)}</strong></div><div className="rounded border bg-green-50 p-3"><span className="text-xs text-gray-500">Entradas</span><strong className="mt-1 block text-green-700">{formatAmount(totals?.inflow)}</strong></div><div className="rounded border bg-red-50 p-3"><span className="text-xs text-gray-500">Saídas</span><strong className="mt-1 block text-red-700">{formatAmount(totals?.outflow)}</strong></div><div className="rounded border bg-blue-50 p-3"><span className="text-xs text-gray-500">Saldo final</span><strong className="mt-1 block text-blue-700">{formatAmount(totals?.closingBalance)}</strong></div></div>
      <div className="rounded border border-indigo-100 bg-indigo-50/50 p-4"><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold text-gray-900">Composição do período</h3><span className={`text-sm font-medium ${Math.abs(cashDifference) < 0.01 ? 'text-green-700' : 'text-amber-700'}`}>{Math.abs(cashDifference) < 0.01 ? 'Caixa consistente' : 'Revisão necessária'}</span></div><p className="mt-1 text-xs text-gray-600">{overview.periodFrom} a {overview.periodUntil} · saldo inicial + entradas − saídas = saldo final</p><div className="mt-3 overflow-x-auto"><table className="min-w-full text-left text-sm"><thead><tr className="border-b text-xs uppercase text-gray-500"><th className="px-2 py-2">Tipo</th><th className="px-2 py-2 text-right">Entradas</th><th className="px-2 py-2 text-right">Saídas</th><th className="px-2 py-2 text-right">Líquido</th></tr></thead><tbody>{movementSummary.map(item => <tr key={item.movementType} className="border-b last:border-0"><td className="px-2 py-2">{labels[item.movementType] ?? item.movementType}</td><td className="px-2 py-2 text-right text-green-700">{formatAmount(item.inflow)}</td><td className="px-2 py-2 text-right text-red-700">{formatAmount(item.outflow)}</td><td className="px-2 py-2 text-right font-medium">{formatAmount(item.inflow - item.outflow)}</td></tr>)}</tbody></table></div>{movementSummary.length === 0 && <p className="mt-2 text-sm text-gray-600">Nenhum movimento classificado no período.</p>}</div>
      <div className="overflow-x-auto"><table className="min-w-full text-left text-sm"><thead><tr className="border-b text-xs uppercase text-gray-500"><th className="px-2 py-2">Data</th><th className="px-2 py-2">Conta</th><th className="px-2 py-2">Tipo</th><th className="px-2 py-2">Descrição</th><th className="px-2 py-2 text-right">Valor</th></tr></thead><tbody>{overview.movements.map(movement => <tr key={movement.cashMovementId} className="border-b last:border-0"><td className="px-2 py-2">{formatDate(movement.occurredAt)}</td><td className="px-2 py-2">{movement.accountName}</td><td className="px-2 py-2">{movement.movementDirection === 'in' ? 'Entrada' : 'Saída'} · {cashMovementLabel(movement)}</td><td className="px-2 py-2">{movement.description ?? '—'}</td><td className={`px-2 py-2 text-right font-medium ${movement.movementDirection === 'in' ? 'text-green-700' : 'text-red-700'}`}>{movement.movementDirection === 'in' ? '+' : '-'} {formatAmount(movement.amount)}</td></tr>)}</tbody></table></div>{overview.movements.length === 0 && <p className="text-sm text-gray-600">Nenhuma movimentação no período.</p>}
    </>}
  </section>;
}

export default function FinancialCashFlowPage() {
  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId ?? '';
  const role = selectedClient?.userRole;

  if (!clientId || role !== 'admin') return <div role="alert" className="p-6">Esta consulta exige vínculo de administrador com a clínica selecionada.</div>;
  if (!isFinancialPreviewEnabled(clientId)) return <div className="rounded-lg border bg-white p-6"><h1 className="text-xl font-semibold">Fluxo de caixa</h1><p className="mt-3">A consulta do novo financeiro ainda não está habilitada para esta clínica.</p></div>;

  return <div className="space-y-5">
    <CashOverviewPanel clientId={clientId} />
  </div>;
}
